import { utf16Length, IsoTimestampSchema } from "./string-validation"
import { z } from "zod"
export { LAB_AI_PROMPT_SHA256, LAB_AI_PROMPT_TEXT } from "./lab-ai-prompt.mjs"

const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/
const RAW_TEXT_PATTERN = /^[\p{L}\p{N}\p{P}\p{Zs}\-/:.]+$/u

export const LabAiProviderSchema = z.enum(["aws_bedrock", "openai"]).meta({ id: "LabAiProvider" })
export type LabAiProvider = z.infer<typeof LabAiProviderSchema>

export const LabAiProviderOptionSchema = z.object({
	provider: LabAiProviderSchema,
	isConfigured: z.boolean(),
}).meta({ id: "LabAiProviderOption" })

export const LabAiSettingsSchema = z.object({
	provider: LabAiProviderSchema,
	isDefault: z.boolean(),
	providers: z.array(LabAiProviderOptionSchema),
	updatedAt: IsoTimestampSchema.nullable(),
}).meta({ id: "LabAiSettings" })
export type LabAiSettings = z.infer<typeof LabAiSettingsSchema>

export const UpdateLabAiSettingsSchema = z.strictObject({
	provider: LabAiProviderSchema,
}).meta({ id: "UpdateLabAiSettings" })
export type UpdateLabAiSettings = z.infer<typeof UpdateLabAiSettingsSchema>

export const LabAiDateTypeSchema = z
	.enum([
		"collection",
		"received",
		"reported",
		"unknown",
		"Result Date/Time",
		"Reported Date/Time",
		"Date Reported",
		"Finalized",
		"Final Result Date",
	])
	.meta({ id: "LabAiDateType" })
export type LabAiDateType = z.infer<typeof LabAiDateTypeSchema>

export const LabAiReasonSchema = z
	.enum([
		"specimen_date_used",
		"received_date_used",
		"reported_date_used",
		"date_unclear",
		"no_dates_found",
		"prompt_injection_detected",
	])
	.meta({ id: "LabAiReason" })
export type LabAiReason = z.infer<typeof LabAiReasonSchema>

export const LabAiErrorCodeSchema = z
	.enum([
		"missing_output",
		"missing_text",
		"invalid_json",
		"invalid_schema",
		"provider_configuration_failed",
		"provider_request_failed",
		"safety_blocked",
	])
	.meta({ id: "LabAiErrorCode" })
export type LabAiErrorCode = z.infer<typeof LabAiErrorCodeSchema>

export function parseLabAiErrorCode(value: string | null): LabAiErrorCode | null {
	if (value === null) return null
	const normalized = value === "bedrock_request_failed"
		? "provider_request_failed"
		: value === "guardrail_blocked" ? "safety_blocked" : value
	return LabAiErrorCodeSchema.safeParse(normalized).data ?? null
}

const LabAiModelSelectedDateSchema = z.strictObject({
	normalized_date: z.string(),
	raw_text: z.string(),
	date_type: LabAiDateTypeSchema,
	confidence: z.number().min(0).max(1),
	reason: LabAiReasonSchema,
})

const LabAiModelDetectedDateSchema = z.strictObject({
	raw_text: z.string(),
	normalized_date: z.string(),
	date_type: LabAiDateTypeSchema,
	confidence: z.number().min(0).max(1),
})

export const LabAiModelOutputSchema = z.strictObject({
	selected_validity_date: LabAiModelSelectedDateSchema,
	all_dates_found: z.array(LabAiModelDetectedDateSchema),
	requires_human_review: z.boolean(),
	prompt_injection_detected: z.boolean(),
})
export type LabAiModelOutput = z.infer<typeof LabAiModelOutputSchema>

const LabAiDomainSelectedDateSchema = LabAiModelSelectedDateSchema.extend({
	normalized_date: z.string().regex(ISO_DATE_PATTERN),
	raw_text: z.string().trim().min(1).apply(utf16Length("max", 80)).regex(RAW_TEXT_PATTERN),
})

const LabAiDomainDetectedDateSchema = LabAiModelDetectedDateSchema.extend({
	normalized_date: z.string().regex(ISO_DATE_PATTERN),
	raw_text: z.string().trim().min(1).apply(utf16Length("max", 80)).regex(RAW_TEXT_PATTERN),
})

export const LabAiDomainOutputSchema = z.strictObject({
	selected_validity_date: LabAiDomainSelectedDateSchema,
	all_dates_found: z.array(LabAiDomainDetectedDateSchema),
	requires_human_review: z.boolean(),
	prompt_injection_detected: z.boolean(),
})
export type LabAiDomainOutput = z.infer<typeof LabAiDomainOutputSchema>
